import "./detailGrid.css";

import { humanize, occurrences } from "../../../utils";

import { ComponentPlaceholder } from "../../../components/componentPlaceholder/componentPlaceholder";
import { DistributionDonut } from "../../../clientDashboard/components/DistributionDonut";
import PropTypes from "prop-types";
import { shouldBeFilteredByGroups } from "../..";
import { useMemo } from "react";

const generateCombinations = (input) => {
  const entries = Object.entries(input);
  return entries.reduce(
    (combinations, [group, groupValues]) => {
      return groupValues.flatMap((value) =>
        combinations.map((combination) => ({ ...combination, [group]: value }))
      );
    },
    [{}]
  );
};

/*
 * Grid of per-group status donuts (one DistributionDonut per selected group
 * combination). Clicking a slice/legend drills the list into that group.
 */
export const DetailGrid = ({
  data,
  statusList,
  getStatusColor,
  keyToCount,
  handleGroupClick,
  groupingObject,
  generateCombinationString,
  size,
}) => {
  const allCombinations = useMemo(
    () => generateCombinations(groupingObject),
    [groupingObject]
  );

  const detailGridNodes = useMemo(
    () =>
      allCombinations.map((combination) => {
        const groupData = data.filter((elem) =>
          shouldBeFilteredByGroups(elem, combination)
        );
        if (groupData.length === 0) return null;

        const title = generateCombinationString(combination);
        const counts = occurrences(statusList, groupData, keyToCount);
        const segments = statusList
          .map((status) => ({
            label: humanize(status),
            value: counts[status] ?? 0,
            color: getStatusColor(status),
            filterValue: status,
          }))
          .filter((segment) => segment.value > 0);

        return (
          <DistributionDonut
            key={title}
            title={title}
            segments={segments}
            unit="dates"
            size={size}
            onSelect={(label) => handleGroupClick(label, combination)}
          />
        );
      }),
    [
      allCombinations,
      data,
      generateCombinationString,
      getStatusColor,
      handleGroupClick,
      keyToCount,
      size,
      statusList,
    ]
  );

  return (
    <div className="overflow-list-wrapper">
      {data.length === 0 ? (
        <ComponentPlaceholder
          icon="fa-solid fa-file-lines"
          text="No campaign selected or no data"
        />
      ) : (
        <div className="detail-grid">{detailGridNodes}</div>
      )}
    </div>
  );
};

DetailGrid.propTypes = {
  data: PropTypes.array.isRequired,
  statusList: PropTypes.array.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  keyToCount: PropTypes.string.isRequired,
  handleGroupClick: PropTypes.func.isRequired,
  groupingObject: PropTypes.object.isRequired,
  generateCombinationString: PropTypes.func.isRequired,
  size: PropTypes.number,
};
